import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import Layout from "../components/Layout";
import SpinWheel from "../components/SpinWheel";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Sparkles, Plus, Trash2, Copy, Eye, EyeOff, BarChart3, Hash, AlertCircle, CheckCircle, X, Download, Pause, Play, Award, ExternalLink } from "lucide-react";
import WheelShareBuilder from "../components/WheelShareBuilder";

const MAX_SEGMENTS = 30;
const MIN_SEGMENTS = 2;

export default function SkaperLykkehjul() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [wheels, setWheels] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // wheel id som redigeres / 'new'
  const [statsOpen, setStatsOpen] = useState(null); // wheel id
  const [codesOpen, setCodesOpen] = useState(null); // wheel id
  const [view, setView] = useState("hjul"); // "hjul" | "vinnere"

  useEffect(() => {
    if (!loading) {
      if (!user) { nav("/?tab=login"); return; }
      if (!user.is_creator) { nav("/bli-skaper"); return; }
    }
  }, [user, loading, nav]);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/me/wheels");
      setWheels(r.data.results || []);
    } finally { setBusy(false); }
  };

  useEffect(() => { if (user?.is_creator) load(); }, [user?.is_creator]);

  const activeCount = wheels.filter(w => w.status === "active").length;

  return (
    <Layout>
      <div className="mb-6">
        <Link to={`/skaper/${user?.username || ""}`} className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] hover:text-[var(--cream)]">
          ← TILBAKE TIL SKAPERSIDEN
        </Link>
        <div className="flex items-center justify-between flex-wrap gap-3 mt-3">
          <h1 className="font-fraunces italic text-4xl text-[var(--cream)] flex items-center gap-3">
            <Sparkles className="h-7 w-7 text-[var(--magenta)]" /> Mitt lykkehjul
          </h1>
          <button
            onClick={() => setEditing("new")}
            disabled={activeCount >= 3}
            className="px-4 py-2 text-xs font-meta tracking-[0.18em] bg-[var(--magenta)] text-black disabled:opacity-50 flex items-center gap-2"
            data-testid="new-wheel-btn"
          >
            <Plus className="h-4 w-4" /> NYTT HJUL
          </button>
        </div>
        <p className="text-[var(--cream-soft)] text-sm mt-2 font-manrope max-w-2xl">
          Sett opp lykkehjul med dine egne premier. Generer engangskoder du selger på dine eksterne plattformer (OnlyFans, Patreon osv).
          Premier er gaver/innhold du leverer selv via melding etter at noen har vunnet.
          <span className="block mt-1 text-[var(--cream-faint)]">
            {activeCount}/3 aktive hjul brukt
          </span>
        </p>
      </div>

      {/* Tab-bar: Hjul vs Vinnere */}
      <div className="border-y border-white/10 flex gap-2 mb-6" data-testid="lykkehjul-tabs">
        <button
          onClick={() => setView("hjul")}
          className={`px-4 py-3 font-meta text-xs tracking-[0.18em] transition-colors ${
            view === "hjul"
              ? "text-[var(--magenta)] border-b-2 border-[var(--magenta)]"
              : "text-[var(--cream-soft)] hover:text-[var(--cream)]"
          }`}
          data-testid="tab-hjul"
        >
          <Sparkles className="inline h-3.5 w-3.5 mr-2 align-text-bottom" />
          MINE HJUL
        </button>
        <button
          onClick={() => setView("vinnere")}
          className={`px-4 py-3 font-meta text-xs tracking-[0.18em] transition-colors ${
            view === "vinnere"
              ? "text-[var(--magenta)] border-b-2 border-[var(--magenta)]"
              : "text-[var(--cream-soft)] hover:text-[var(--cream)]"
          }`}
          data-testid="tab-vinnere"
        >
          <Award className="inline h-3.5 w-3.5 mr-2 align-text-bottom" />
          VINNERE (30 DAGER)
        </button>
      </div>

      {view === "vinnere" ? (
        <WinnersPanel />
      ) : (
        <>
      {/* Iter 50: Del-melding-bygger – kun synlig når det finnes aktive hjul */}
      <WheelShareBuilder wheels={wheels} creatorUsername={user?.creator_handle || user?.username} />

      {busy && wheels.length === 0 && (
        <div className="font-meta text-[var(--cream-soft)] py-10">Laster…</div>
      )}

      {!busy && wheels.length === 0 && (
        <div className="border border-dashed border-white/15 p-10 text-center" data-testid="empty-state">
          <Sparkles className="h-10 w-10 text-[var(--magenta)] mx-auto mb-3 opacity-50" />
          <p className="text-[var(--cream)] font-manrope mb-4">
            Du har ikke laget et lykkehjul ennå.
          </p>
          <button
            onClick={() => setEditing("new")}
            className="px-5 py-2 text-xs font-meta tracking-[0.18em] bg-[var(--magenta)] text-black"
          >
            OPPRETT FØRSTE HJULET
          </button>
        </div>
      )}

      <div className="space-y-4">
        {wheels.map((w) => (
          <WheelCard
            key={w.id}
            wheel={w}
            onChange={load}
            onEdit={() => setEditing(w.id)}
            onStats={() => setStatsOpen(w.id)}
            onCodes={() => setCodesOpen(w.id)}
          />
        ))}
      </div>
        </>
      )}

      {editing && (
        <WheelEditor
          wheelId={editing === "new" ? null : editing}
          existing={editing === "new" ? null : wheels.find(w => w.id === editing)}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {statsOpen && (
        <StatsModal wheelId={statsOpen} onClose={() => setStatsOpen(null)} />
      )}

      {codesOpen && (
        <CodesModal wheelId={codesOpen} wheel={wheels.find(w => w.id === codesOpen)} onClose={() => setCodesOpen(null)} />
      )}
    </Layout>
  );
}

function WheelCard({ wheel, onChange, onEdit, onStats, onCodes }) {
  const directLink = `${window.location.origin}/lykkehjul/${wheel.slug}`;
  const toggleStatus = async () => {
    const newStatus = wheel.status === "active" ? "paused" : "active";
    try {
      await api.patch(`/me/wheels/${wheel.id}`, { status: newStatus });
      toast.success(newStatus === "active" ? "Hjulet er aktivt" : "Hjulet er pauset");
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke oppdatere");
    }
  };
  const toggleVisible = async () => {
    try {
      await api.patch(`/me/wheels/${wheel.id}`, { visible_on_page: !wheel.visible_on_page });
      onChange();
    } catch {}
  };
  const archive = async () => {
    if (!confirm("Arkivér dette hjulet? Eksisterende vinner-statistikk beholdes, men ingen nye spinn er mulig.")) return;
    try {
      await api.delete(`/me/wheels/${wheel.id}`);
      toast.success("Arkivert");
      onChange();
    } catch {}
  };
  const copyLink = () => {
    navigator.clipboard.writeText(directLink);
    toast.success("Direktelink kopiert");
  };

  return (
    <div className="border border-white/10 bg-[var(--noir-elev)] p-5" data-testid={`wheel-card-${wheel.id}`}>
      <div className="flex items-start gap-5 flex-wrap">
        {/* Mini preview */}
        <div className="shrink-0" style={{ pointerEvents: "none" }}>
          <SpinWheel segments={wheel.segments} size={120} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="font-fraunces italic text-xl text-[var(--cream)] truncate">{wheel.title}</h3>
            <span className={`text-[10px] font-meta tracking-[0.15em] px-2 py-0.5 ${
              wheel.status === "active" ? "bg-[#7FA88E]/20 text-[#7FA88E]"
                : "bg-white/10 text-[var(--cream-faint)]"
            }`}>{wheel.status === "active" ? "AKTIV" : "PAUSET"}</span>
          </div>
          {wheel.description && (
            <p className="text-sm text-[var(--cream-soft)] font-manrope mb-2">{wheel.description}</p>
          )}
          <div className="text-xs text-[var(--cream-faint)] font-meta flex flex-wrap gap-3">
            <span>{wheel.segments?.length} segmenter</span>
            {wheel.purchase_url && (
              <span className="text-[#7FA88E]">✓ Kjøp-link satt</span>
            )}
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          <button onClick={onCodes} data-testid={`codes-${wheel.id}`} className="px-3 py-1.5 text-xs font-meta tracking-[0.15em] border border-white/15 text-[var(--cream)] hover:border-[var(--magenta)]/60 flex items-center gap-1.5">
            <Hash className="h-3.5 w-3.5" /> KODER
          </button>
          <button onClick={onStats} data-testid={`stats-${wheel.id}`} className="px-3 py-1.5 text-xs font-meta tracking-[0.15em] border border-white/15 text-[var(--cream)] hover:border-[var(--magenta)]/60 flex items-center gap-1.5">
            <BarChart3 className="h-3.5 w-3.5" /> STATISTIKK
          </button>
          <button onClick={onEdit} data-testid={`edit-${wheel.id}`} className="px-3 py-1.5 text-xs font-meta tracking-[0.15em] border border-white/15 text-[var(--cream)] hover:border-[var(--magenta)]/60">
            REDIGER
          </button>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-white/5 flex gap-2 flex-wrap items-center">
        <button onClick={toggleStatus} className="px-2 py-1 text-[11px] font-meta text-[var(--cream-soft)] hover:text-[var(--cream)] flex items-center gap-1">
          {wheel.status === "active" ? <><Pause className="h-3 w-3" /> Pause</> : <><Play className="h-3 w-3" /> Aktiver</>}
        </button>
        <button onClick={toggleVisible} className="px-2 py-1 text-[11px] font-meta text-[var(--cream-soft)] hover:text-[var(--cream)] flex items-center gap-1">
          {wheel.visible_on_page ? <><Eye className="h-3 w-3" /> Synlig på siden</> : <><EyeOff className="h-3 w-3" /> Skjult på siden</>}
        </button>
        <div className="flex-1 min-w-0" />
        <code className="text-[11px] font-mono bg-[var(--noir)] px-2 py-1 text-[var(--cream-soft)] truncate max-w-xs">{directLink}</code>
        <button onClick={copyLink} className="p-1.5 text-[var(--cream-soft)] hover:text-[var(--magenta)]" title="Kopier link">
          <Copy className="h-3.5 w-3.5" />
        </button>
        <button onClick={archive} className="p-1.5 text-[var(--cream-faint)] hover:text-red-400" title="Arkivér">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function WheelEditor({ wheelId, existing, onClose, onSaved }) {
  const isNew = !wheelId;
  const [title, setTitle] = useState(existing?.title || "");
  const [description, setDescription] = useState(existing?.description || "");
  const [purchaseUrl, setPurchaseUrl] = useState(existing?.purchase_url || "");
  // locked=true betyr at bruker har redigert vekten manuelt;
  // unlocked-segmenter justeres automatisk for å holde sum=100.
  const [segments, setSegments] = useState(
    existing?.segments?.map(s => ({ label: s.label, weight: s.weight, locked: true, auto_respin: !!s.auto_respin })) ||
    [{ label: "", weight: 50, locked: false, auto_respin: false }, { label: "", weight: 50, locked: false, auto_respin: false }]
  );
  const [busy, setBusy] = useState(false);
  const [warning, setWarning] = useState(null);

  // Distribuer tilgjengelig vekt (100 - sum(låste)) jevnt over ulåste segmenter.
  // Clamp til 1-50 per segment. Hvis ingen ulåste – returner uendret.
  const rebalanceUnlocked = (segs) => {
    const unlockedIdx = segs.map((s, i) => (!s.locked ? i : -1)).filter(i => i >= 0);
    if (unlockedIdx.length === 0) return segs;
    const lockedSum = segs.filter(s => s.locked).reduce((acc, s) => acc + (Number(s.weight) || 0), 0);
    const remainder = Math.max(0, 100 - lockedSum);
    const n = unlockedIdx.length;
    const base = Math.floor(remainder / n);
    const extra = remainder - base * n;
    const out = [...segs];
    unlockedIdx.forEach((idx, k) => {
      const raw = base + (k < extra ? 1 : 0);
      const w = Math.max(1, Math.min(50, raw));
      out[idx] = { ...out[idx], weight: w };
    });
    return out;
  };

  const total = segments.reduce((sum, s) => sum + (Number(s.weight) || 0), 0);
  // Total vekt på respin-segmenter – maks 50% kombinert for å unngå evig løkke
  const respinWeight = segments.reduce((sum, s) => sum + (s.auto_respin ? (Number(s.weight) || 0) : 0), 0);
  // Må ha minst ett ikke-respin segment ellers har ingen brukere noen sjanse for ekte premie
  const hasRealPrize = segments.some(s => !s.auto_respin);
  const respinValid = respinWeight <= 50 && hasRealPrize;
  const valid = total === 100 && segments.length >= MIN_SEGMENTS && segments.length <= MAX_SEGMENTS &&
                segments.every(s => s.label.trim() && Number(s.weight) >= 1 && Number(s.weight) <= 50) &&
                respinValid && title.trim();

  const addSeg = () => {
    if (segments.length >= MAX_SEGMENTS) return;
    const next = [...segments, { label: "", weight: 1, locked: false, auto_respin: false }];
    setSegments(rebalanceUnlocked(next));
  };
  const removeSeg = (i) => {
    if (segments.length <= MIN_SEGMENTS) return;
    const next = segments.filter((_, idx) => idx !== i);
    setSegments(rebalanceUnlocked(next));
  };
  const updateLabel = (i, label) => {
    setSegments(segments.map((s, idx) => idx === i ? { ...s, label } : s));
  };
  const updateWeight = (i, weightRaw) => {
    const weight = Math.max(1, Math.min(50, Number(weightRaw) || 0));
    // Marker manuelt redigert (locked) og rebalansér resten.
    const next = segments.map((s, idx) => idx === i ? { ...s, weight, locked: true } : s);
    setSegments(rebalanceUnlocked(next));
  };
  const unlockSeg = (i) => {
    const next = segments.map((s, idx) => idx === i ? { ...s, locked: false } : s);
    setSegments(rebalanceUnlocked(next));
  };
  const toggleRespin = (i) => {
    setSegments(segments.map((s, idx) => idx === i ? { ...s, auto_respin: !s.auto_respin } : s));
  };
  // "Balanser 100%" – lås opp alle og fordel jevnt
  const balance = () => {
    const next = segments.map(s => ({ ...s, locked: false }));
    setSegments(rebalanceUnlocked(next));
  };

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        purchase_url: purchaseUrl.trim(),
        segments: segments.map(s => ({
          label: s.label.trim(),
          weight: Number(s.weight),
          auto_respin: !!s.auto_respin,
        })),
      };
      if (isNew) {
        await api.post("/me/wheels", payload);
        toast.success("Hjulet er opprettet");
      } else {
        await api.patch(`/me/wheels/${wheelId}`, payload);
        toast.success("Hjulet er oppdatert");
      }
      onSaved();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === "string" && detail.includes("Kan ikke endre segmenter")) {
        setWarning(detail);
      } else {
        toast.error(detail || "Kunne ikke lagre");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-[var(--noir-elev)] border border-white/10 p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-fraunces italic text-2xl text-[var(--cream)]">
            {isNew ? "Nytt lykkehjul" : "Rediger hjul"}
          </h2>
          <button onClick={onClose}><X className="h-5 w-5 text-[var(--cream-soft)]" /></button>
        </div>

        {warning && (
          <div className="mb-4 border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200 flex gap-2">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{warning}</span>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)] mb-1">TITTEL</label>
            <input value={title} onChange={e => setTitle(e.target.value)} maxLength={80}
              placeholder="Eks. Sommerhjulet"
              className="w-full bg-[var(--noir)] border border-white/15 px-3 py-2 text-[var(--cream)] outline-none focus:border-[var(--magenta)]"
              data-testid="title-input"
            />
          </div>
          <div>
            <label className="block font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)] mb-1">BESKRIVELSE (valgfri)</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} maxLength={300} rows={2}
              placeholder="Kort tekst som vises på spin-siden"
              className="w-full bg-[var(--noir)] border border-white/15 px-3 py-2 text-[var(--cream)] outline-none focus:border-[var(--magenta)]"
              data-testid="desc-input"
            />
          </div>

          <div>
            <label className="block font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)] mb-1">
              KJØP-LINK (valgfri) – f.eks. til OnlyFans-store hvor du selger spinn-koder
            </label>
            <input type="url" value={purchaseUrl} onChange={e => setPurchaseUrl(e.target.value)} maxLength={500}
              placeholder="https://onlyfans.com/maja/store"
              className="w-full bg-[var(--noir)] border border-white/15 px-3 py-2 text-[var(--cream)] outline-none focus:border-[var(--magenta)]"
              data-testid="purchase-url-input"
            />
            <p className="font-meta text-[10px] text-[var(--cream-faint)] mt-1">
              Vises som «Kjøp spinn-kode →»-knapp over hjulet på spin-siden. Brukere som mangler kode kan klikke seg dit.
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)]">
                SEGMENTER ({segments.length}/{MAX_SEGMENTS}) – sum vekter: <span className={total === 100 ? "text-[#7FA88E]" : "text-[var(--magenta)]"}>{total}/100</span>
                {respinWeight > 0 && (
                  <span className={`ml-3 ${respinWeight > 50 ? "text-[var(--magenta)]" : "text-[#5BA5C9]"}`}>
                    · respin {respinWeight}%
                  </span>
                )}
              </label>
              <div className="flex gap-2">
                <button onClick={balance} className="text-[10px] font-meta text-[var(--magenta)] hover:underline">BALANSER 100%</button>
                <button onClick={addSeg} disabled={segments.length >= MAX_SEGMENTS} className="text-[10px] font-meta text-[var(--cream)] disabled:opacity-30 flex items-center gap-1">
                  <Plus className="h-3 w-3" /> Legg til
                </button>
              </div>
            </div>
            <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
              {segments.map((s, i) => (
                <div key={i} className={`flex gap-2 items-center ${s.auto_respin ? "border-l-2 border-[#5BA5C9]/60 pl-2" : ""}`} data-testid={`segment-${i}`}>
                  <span className="font-meta text-[10px] text-[var(--cream-faint)] w-6">{i + 1}.</span>
                  <input value={s.label} onChange={e => updateLabel(i, e.target.value)} maxLength={60}
                    placeholder={s.auto_respin ? "Eks. Spin igjen, Bonusrunde" : "Premie-beskrivelse"}
                    className="flex-1 bg-[var(--noir)] border border-white/15 px-3 py-2 text-sm text-[var(--cream)] outline-none focus:border-[var(--magenta)]"
                  />
                  <input type="number" value={s.weight} onChange={e => updateWeight(i, e.target.value)}
                    min={1} max={50}
                    className={`w-16 bg-[var(--noir)] border px-2 py-2 text-sm text-center outline-none ${
                      s.locked
                        ? "border-[var(--magenta)]/50 text-[var(--cream)]"
                        : "border-white/15 text-[var(--cream-soft)] focus:border-[var(--magenta)]"
                    }`}
                    title={s.locked ? "Manuelt satt – klikk lås-ikonet for å gjøre automatisk" : "Justeres automatisk"}
                  />
                  <span className="font-meta text-xs text-[var(--cream-faint)]">%</span>
                  {s.locked ? (
                    <button onClick={() => unlockSeg(i)} title="Slipp manuell verdi – automatisk justering"
                      className="text-[var(--magenta)] hover:text-[var(--cream)] text-[10px] font-meta px-1">
                      LÅST
                    </button>
                  ) : (
                    <span className="text-[10px] font-meta text-[var(--cream-faint)] px-1" title="Justeres automatisk">AUTO</span>
                  )}
                  <button
                    type="button"
                    onClick={() => toggleRespin(i)}
                    title={s.auto_respin
                      ? "Auto-respin PÅ — utløser et nytt spinn automatisk uten å bruke ny kode"
                      : "Slå på auto-respin — gir spinneren et nytt spinn uten å bruke ny kode"}
                    className={`text-[14px] px-1.5 py-0.5 rounded-sm transition-colors ${
                      s.auto_respin
                        ? "bg-[#5BA5C9]/20 text-[#5BA5C9] border border-[#5BA5C9]/40"
                        : "text-[var(--cream-faint)] hover:text-[#5BA5C9] border border-transparent"
                    }`}
                    data-testid={`respin-toggle-${i}`}
                  >
                    🔁
                  </button>
                  <button onClick={() => removeSeg(i)} disabled={segments.length <= MIN_SEGMENTS} className="text-[var(--cream-faint)] hover:text-red-400 disabled:opacity-20">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <p className="font-meta text-[10px] text-[var(--cream-faint)] mt-2 leading-relaxed">
              Vekt 1–50% per segment. AUTO-segmenter justeres automatisk når du endrer LÅST-tall eller legger til/fjerner.
              Klikk «LÅST» for å frigjøre et segment til auto. «Balanser 100%» nullstiller alt til jevn fordeling.
              <br />
              <span className="text-[#5BA5C9]">🔁 Auto-respin:</span> spinneren får et nytt spinn automatisk – uten å bruke en ny kode og uten varsler. Maks 50% kombinert vekt på respin-felter. Samme respin-felt kan treffes maks 3 ganger på rad før spinnet tvinges over på en ekte premie.
            </p>
            {respinWeight > 50 && (
              <div className="mt-2 border border-[var(--magenta)]/40 bg-[var(--magenta)]/10 p-2 text-xs text-[var(--cream)] font-medium border-l-2 border-[var(--magenta)] pl-3">
                Respin-segmenter har totalt {respinWeight}% – maks tillatt er 50%.
              </div>
            )}
            {!hasRealPrize && (
              <div className="mt-2 border border-[var(--magenta)]/40 bg-[var(--magenta)]/10 p-2 text-xs text-[var(--cream)] font-medium border-l-2 border-[var(--magenta)] pl-3">
                Du må ha minst ett segment som IKKE er respin – ellers er det ingen ekte gevinst å lande på.
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-xs font-meta tracking-[0.18em] border border-white/15 text-[var(--cream-soft)]">AVBRYT</button>
          <button onClick={submit} disabled={!valid || busy} className="px-4 py-2 text-xs font-meta tracking-[0.18em] bg-[var(--magenta)] text-black disabled:opacity-50" data-testid="save-wheel">
            {busy ? "LAGRER…" : (isNew ? "OPPRETT HJUL" : "LAGRE")}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatsModal({ wheelId, onClose }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get(`/me/wheels/${wheelId}/stats`).then(r => setStats(r.data)).catch(() => {});
  }, [wheelId]);

  return (
    <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[var(--noir-elev)] border border-white/10 p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="stats-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-fraunces italic text-2xl text-[var(--cream)]">Statistikk</h2>
          <button onClick={onClose}><X className="h-5 w-5 text-[var(--cream-soft)]" /></button>
        </div>

        {!stats ? <div className="font-meta text-[var(--cream-soft)] py-8 text-center">Laster…</div> : (
          <>
            <div className="grid grid-cols-3 gap-3 mb-6">
              <StatBox label="Koder generert" value={stats.codes_total} />
              <StatBox label="Spinn brukt" value={stats.codes_redeemed} accent="#D11A5B" />
              <StatBox label="Igjen" value={stats.codes_remaining} />
            </div>

            <h3 className="font-fraunces italic text-lg text-[var(--cream)] mb-3">Fordeling per premie</h3>
            <div className="border border-white/10 divide-y divide-white/5 mb-6">
              {stats.distribution.length === 0 ? (
                <div className="p-4 text-sm text-[var(--cream-faint)] font-manrope text-center">Ingen spinn enda</div>
              ) : stats.distribution.map((r) => {
                const pct = stats.codes_redeemed > 0 ? ((r.count / stats.codes_redeemed) * 100).toFixed(1) : "0";
                return (
                  <div key={r.segment_id} className="flex items-center justify-between px-3 py-2">
                    <span className="text-sm text-[var(--cream)] truncate">{r.label}</span>
                    <div className="text-right">
                      <span className="font-fraunces italic text-xl text-[var(--cream)] tabular-nums">{r.count}</span>
                      <span className="font-meta text-[10px] text-[var(--cream-faint)] ml-2">{pct}%</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <h3 className="font-fraunces italic text-lg text-[var(--cream)] mb-3 flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-[var(--magenta)]" /> Siste vinnere
            </h3>
            {stats.recent_spins.length === 0 ? (
              <div className="text-sm text-[var(--cream-faint)] font-manrope text-center py-4">Ingen vinnere enda</div>
            ) : (
              <div className="border border-white/10 divide-y divide-white/5">
                {stats.recent_spins.map((sp) => (
                  <div key={sp.id} className="flex items-center justify-between px-3 py-2 gap-2">
                    <div className="min-w-0">
                      <div className="text-sm text-[var(--cream)] truncate">{sp.winner?.username}</div>
                      <div className="font-meta text-[10px] text-[var(--cream-faint)]">{sp.segment_label}</div>
                    </div>
                    <div className="font-meta text-[10px] text-[var(--cream-faint)] shrink-0">
                      {new Date(sp.created_at).toLocaleString("nb-NO", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function StatBox({ label, value, accent }) {
  return (
    <div className="border border-white/10 bg-[var(--noir)] p-3" style={accent ? { borderLeft: `3px solid ${accent}` } : {}}>
      <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)] mb-1">{label.toUpperCase()}</div>
      <div className="font-fraunces italic text-3xl text-[var(--cream)] tabular-nums">{value}</div>
    </div>
  );
}

function CodesModal({ wheelId, wheel, onClose }) {
  const [codes, setCodes] = useState([]);
  const [count, setCount] = useState(10);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all"); // all | unused | used

  const load = async () => {
    const params = filter === "unused" ? "?redeemed=false" : filter === "used" ? "?redeemed=true" : "";
    const r = await api.get(`/me/wheels/${wheelId}/codes${params}`);
    setCodes(r.data.results || []);
  };
  useEffect(() => { load(); }, [wheelId, filter]);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/me/wheels/${wheelId}/codes`, { count: Number(count) });
      toast.success(`${r.data.count} koder generert`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke generere");
    } finally { setBusy(false); }
  };

  const copyAll = () => {
    const text = codes.filter(c => !c.redeemed).map(c => c.code).join("\n");
    navigator.clipboard.writeText(text);
    toast.success(`${codes.filter(c => !c.redeemed).length} ubrukte koder kopiert`);
  };

  const downloadCSV = () => {
    const csv = "code,status,created_at,redeemed_at\n" +
      codes.map(c => `${c.code},${c.redeemed ? "brukt" : "ubrukt"},${c.created_at || ""},${c.redeemed_at || ""}`).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `koder-${wheel?.slug || wheelId}.csv`;
    a.click();
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[var(--noir-elev)] border border-white/10 p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="codes-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-fraunces italic text-2xl text-[var(--cream)]">Koder</h2>
          <button onClick={onClose}><X className="h-5 w-5 text-[var(--cream-soft)]" /></button>
        </div>

        <div className="border border-white/10 bg-[var(--noir)] p-3 mb-4 flex items-center gap-2 flex-wrap">
          <span className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)]">GENERER NYE:</span>
          <input type="number" min={1} max={100} value={count} onChange={e => setCount(Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
            className="w-20 bg-transparent border border-white/15 px-2 py-1 text-sm text-[var(--cream)] outline-none focus:border-[var(--magenta)]"
            data-testid="generate-count"
          />
          <button onClick={generate} disabled={busy} className="px-3 py-1.5 text-xs font-meta tracking-[0.18em] bg-[var(--magenta)] text-black disabled:opacity-50" data-testid="generate-btn">
            {busy ? "GENERERER…" : "GENERER"}
          </button>
          <span className="font-meta text-[10px] text-[var(--cream-faint)] ml-auto">Maks 100 pr gang</span>
        </div>

        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {[
            { v: "all", l: "Alle" },
            { v: "unused", l: "Ubrukte" },
            { v: "used", l: "Brukte" },
          ].map(o => (
            <button key={o.v} onClick={() => setFilter(o.v)}
              className={`px-2 py-1 text-[11px] font-meta tracking-[0.15em] border ${
                filter === o.v ? "border-[var(--magenta)] text-[var(--magenta)] bg-[var(--magenta)]/10" : "border-white/15 text-[var(--cream-soft)]"
              }`}
            >
              {o.l.toUpperCase()} ({codes.filter(c => o.v === "all" || (o.v === "used" ? c.redeemed : !c.redeemed)).length})
            </button>
          ))}
          <div className="flex-1" />
          <button onClick={copyAll} className="text-[11px] font-meta text-[var(--cream-soft)] hover:text-[var(--cream)] flex items-center gap-1">
            <Copy className="h-3 w-3" /> Kopier ubrukte
          </button>
          <button onClick={downloadCSV} className="text-[11px] font-meta text-[var(--cream-soft)] hover:text-[var(--cream)] flex items-center gap-1">
            <Download className="h-3 w-3" /> CSV
          </button>
        </div>

        <div className="border border-white/10 divide-y divide-white/5 max-h-[400px] overflow-y-auto">
          {codes.length === 0 ? (
            <div className="p-4 text-sm text-[var(--cream-faint)] text-center font-manrope">Ingen koder</div>
          ) : codes.map(c => (
            <div key={c.id} className="flex items-center justify-between px-3 py-2 gap-2" data-testid={`code-row-${c.code}`}>
              <code className="font-mono text-sm tracking-[0.2em] text-[var(--cream)]">{c.code}</code>
              {c.redeemed ? (
                <span className="font-meta text-[10px] text-[var(--cream-faint)]">BRUKT</span>
              ) : (
                <span className="font-meta text-[10px] text-[#7FA88E]">UBRUKT</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Vinneroversikt – siste 30 dager
// ---------------------------------------------------------------------------
function WinnersPanel() {
  const [rows, setRows] = useState([]);
  const [loadingWinners, setLoadingWinners] = useState(true);
  const [hideDelivered, setHideDelivered] = useState(false);

  const load = async () => {
    setLoadingWinners(true);
    try {
      const r = await api.get("/me/wheel-winners?days=30");
      setRows(r.data.results || []);
    } catch (e) {
      toast.error("Kunne ikke hente vinnerliste.");
    } finally {
      setLoadingWinners(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleDelivered = async (spinId, currentlyDelivered) => {
    try {
      await api.post(`/me/wheel-spins/${spinId}/delivered`, { delivered: !currentlyDelivered });
      setRows((prev) => prev.map((r) =>
        r.spin_id === spinId
          ? { ...r, delivered: !currentlyDelivered, delivered_at: !currentlyDelivered ? new Date().toISOString() : null }
          : r
      ));
      toast.success(!currentlyDelivered ? "Markert som levert." : "Markert som ikke levert.");
    } catch (e) {
      toast.error("Kunne ikke oppdatere status.");
    }
  };

  const visibleRows = hideDelivered ? rows.filter((r) => !r.delivered) : rows;
  const undelivered = rows.filter((r) => !r.delivered).length;

  return (
    <div data-testid="winners-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div className="text-[var(--cream-soft)] text-sm font-manrope">
          <strong className="text-[var(--cream)]">{rows.length}</strong> vinnere siste 30 dager
          {undelivered > 0 && (
            <span className="ml-3 text-[var(--magenta)]">
              ({undelivered} ikke levert ennå)
            </span>
          )}
        </div>
        <label className="font-meta text-xs text-[var(--cream-soft)] flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={hideDelivered}
            onChange={(e) => setHideDelivered(e.target.checked)}
            data-testid="hide-delivered-checkbox"
          />
          SKJUL LEVERTE
        </label>
      </div>

      {loadingWinners ? (
        <div className="font-meta text-[var(--cream-soft)] py-10">Laster …</div>
      ) : visibleRows.length === 0 ? (
        <div className="border border-dashed border-white/15 p-10 text-center" data-testid="winners-empty">
          <Award className="h-10 w-10 text-[var(--magenta)] mx-auto mb-3 opacity-50" />
          <p className="text-[var(--cream)] font-manrope">
            {hideDelivered ? "Ingen ikke-leverte gevinster." : "Ingen vinnere de siste 30 dagene."}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-white/10">
          <table className="w-full text-sm font-manrope">
            <thead className="bg-[var(--noir-elev)]">
              <tr className="text-left text-[var(--cream-soft)] font-meta text-[10px] tracking-[0.15em] uppercase">
                <th className="px-3 py-2.5">Dato</th>
                <th className="px-3 py-2.5">Hjul</th>
                <th className="px-3 py-2.5">Vinner</th>
                <th className="px-3 py-2.5">Gevinst</th>
                <th className="px-3 py-2.5">Kode</th>
                <th className="px-3 py-2.5">Claim-ID</th>
                <th className="px-3 py-2.5 text-center">Levert</th>
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((r) => (
                <tr
                  key={r.spin_id}
                  className={`border-t border-white/5 ${r.delivered ? "opacity-50" : ""}`}
                  data-testid={`winner-row-${r.spin_id}`}
                >
                  <td className="px-3 py-2.5 whitespace-nowrap text-[var(--cream-soft)] text-xs">
                    {new Date(r.created_at).toLocaleDateString("nb-NO", { day: "2-digit", month: "2-digit", year: "2-digit" })}
                    <div className="text-[10px] text-[var(--cream-faint)]">
                      {new Date(r.created_at).toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-[var(--cream)] text-xs">{r.wheel_title}</td>
                  <td className="px-3 py-2.5 text-[var(--cream)] text-xs">
                    {r.winner_is_anonymous ? (
                      <>
                        <span className="text-[var(--cream-faint)] text-[10px] uppercase mr-1">ext:</span>
                        {r.winner_display}
                        {r.winner_external_platform && (
                          <div className="text-[10px] text-[var(--cream-faint)]">via {r.winner_external_platform}</div>
                        )}
                      </>
                    ) : (
                      <Link to={`/profile/${r.winner_display}`} className="text-[var(--magenta)] hover:underline">
                        @{r.winner_display}
                      </Link>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-[var(--cream)] text-xs italic">{r.segment_label}</td>
                  <td className="px-3 py-2.5 font-mono text-[var(--cream-soft)] text-xs">{r.code}</td>
                  <td className="px-3 py-2.5 font-mono text-[var(--magenta)] text-xs font-bold">{r.claim_id || "—"}</td>
                  <td className="px-3 py-2.5 text-center">
                    <button
                      onClick={() => toggleDelivered(r.spin_id, r.delivered)}
                      className={`inline-flex items-center justify-center h-6 w-6 border ${
                        r.delivered
                          ? "border-emerald-500 bg-emerald-500/20 text-emerald-400"
                          : "border-white/20 text-[var(--cream-faint)] hover:border-[var(--magenta)] hover:text-[var(--magenta)]"
                      }`}
                      title={r.delivered ? "Markert som levert. Klikk for å angre." : "Marker som levert"}
                      data-testid={`toggle-delivered-${r.spin_id}`}
                    >
                      {r.delivered ? <CheckCircle className="h-4 w-4" /> : <X className="h-4 w-4" />}
                    </button>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <Link
                      to={`/lykkehjul/vinst/${r.spin_id}`}
                      className="text-[var(--magenta)] hover:underline inline-flex items-center gap-1 text-xs"
                      data-testid={`open-spin-${r.spin_id}`}
                    >
                      Detaljer <ExternalLink className="h-3 w-3" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
